// ECO / MCO Scenario Comparison — "Why would MCO pay when ECO does not?"
// The unit (APH, acres, share, coverage) is the Calculator tab's §1 table — not repeated here.
// The expected county legs are readonly because they are RMA's guarantee inputs, not assumptions
// this screen owns; the season is stressed with the sliders instead. Layout follows the reference
// app's screen 02 (datanac-core-dbx/products/mco_eco_app_reference/js/comparator.js); crop /
// county / year context comes from the dialog title, so there are no selectors here.

import { useEffect, useMemo, useState } from "react";

import { Alert, Box, Card, LinearProgress, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { buildEcoMcoScenario, scenarioSeed, scenarioStressBounds } from "api-toolkit/mco/mcoEcoScenario";
import { formatNumber, formatPrice } from "utility/formatters";

import { COLOR_ECO, COLOR_MCO } from "./McoBacktestChart";
import McoEcoScenarioChart from "./McoEcoScenarioChart";
import StressSlider from "./StressSlider";

const fm = (v) => "$" + Math.round(Number(v) || 0).toLocaleString("en-US");
const f2 = (v) => formatPrice(Number(v) || 0, 2);

/** "$33.77/ac above expected" / "as expected" — how far a slider sits off the expected leg. */
function deviation(actual, expected, { unit, tolerance, decimals, money = true }) {
  const delta = Number(actual) - Number(expected);
  if (!Number.isFinite(delta) || Math.abs(delta) < tolerance) return { text: "as expected", off: false };
  const amount = (money ? "$" : "") + Math.abs(delta).toFixed(decimals);
  return { text: `${amount}${unit} ${delta > 0 ? "above" : "below"} expected`, off: true };
}

function PaymentCard({ product, color, payment, protection, paymentFactor, watches, estimates }) {
  const pct = Math.round(paymentFactor * 100);
  return (
    <Card
      variant="outlined"
      sx={{ p: 2, borderRadius: 3, flex: "1 1 220px", bgcolor: alpha(color, 0.09), borderColor: alpha(color, 0.25) }}
    >
      <Typography variant="caption" sx={{ color, display: "block" }}>
        {product} {estimates ? "would pay" : "pays"}
      </Typography>
      <Typography variant="h5" sx={{ color, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
        {fm(payment)}
      </Typography>
      <Typography variant="caption" sx={{ color, display: "block" }}>
        {paymentFactor <= 0
          ? `${watches} held above its trigger`
          : paymentFactor >= 1
            ? "the most it can pay"
            : `${pct}% of the most it can pay`}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, Math.max(0, pct))}
        sx={{
          mt: 1.25,
          height: 6,
          borderRadius: 3,
          bgcolor: "common.white",
          "& .MuiLinearProgress-bar": { bgcolor: color },
        }}
      />
      <Typography variant="caption" sx={{ color: alpha(color, 0.8), display: "block", mt: 0.5 }}>
        {fm(protection)} maximum
      </Typography>
    </Card>
  );
}

export default function McoEcoScenarioTab({ mco }) {
  const baseline = mco.stressBaseline;
  const seed = useMemo(() => scenarioSeed(baseline), [baseline]);
  const bounds = useMemo(() => scenarioStressBounds(seed), [seed]);

  const [stress, setStress] = useState(null);

  // Reseed on the Official numbers only — not on every `mco` object identity change, which would
  // snap the sliders back to expected on each poll.
  useEffect(() => {
    if (!baseline || !seed) return;
    setStress({
      harvestPrice: seed.projectedPrice,
      finalYield: seed.expectedYield,
      harvestCosts: seed.expectedCosts,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: seed key is the Official numbers
  }, [baseline?.projectedPrice, baseline?.expectedYield, baseline?.projectedCosts, baseline?.acres]);

  // No units argument: the scenario sizes protection off the baseline's own unit, which is the
  // one the Calculator tab walks through.
  const scenario = useMemo(
    () => (baseline && stress ? buildEcoMcoScenario(baseline, null, stress) : null),
    [baseline, stress]
  );

  if (!baseline) {
    return <Alert severity="info">Scenario comparison unavailable — need Official actuarial data.</Alert>;
  }
  if (!scenario) return null;
  if (!scenario.ready) {
    return <Alert severity="info">{scenario.blockers?.join("; ") || "Scenario comparison unavailable."}</Alert>;
  }

  const { expected } = scenario;
  const priceDev = deviation(scenario.stress.harvestPrice, expected.projectedPrice, {
    unit: "/bu",
    tolerance: 0.05,
    decimals: 2,
  });
  const yieldDev = deviation(scenario.stress.finalYield, expected.countyYield, {
    unit: " bu",
    tolerance: 0.5,
    decimals: 1,
    money: false,
  });
  const costDev = deviation(scenario.stress.harvestCosts, expected.inputCost, {
    unit: "/ac",
    tolerance: 2,
    decimals: 2,
  });

  return (
    <Box>
      <Card variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: "action.hover" }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          {scenario.estimates ? "What the guarantee is expected to be built on" : "What the guarantee is built on"}
        </Typography>
        <Stack direction="row" spacing={4} flexWrap="wrap" rowGap={1.5}>
          {[
            ["Expected county yield", formatNumber(expected.countyYield, 1)],
            ["Projected price", f2(expected.projectedPrice)],
            ["Expected input cost", f2(expected.inputCost)],
          ].map(([label, value]) => (
            <Box key={label}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {label}
              </Typography>
              <Typography variant="body1" sx={{ fontVariantNumeric: "tabular-nums" }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Card>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Then stress the season
      </Typography>
      <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={1} sx={{ mb: 1 }}>
        <StressSlider
          label="Harvest price"
          valueLabel={`${f2(scenario.stress.harvestPrice)}/bu`}
          {...bounds.harvestPrice}
          value={stress.harvestPrice}
          onChange={(v) => setStress((prev) => ({ ...prev, harvestPrice: v }))}
          caption={priceDev.text}
          captionOff={priceDev.off}
        />
        <StressSlider
          label="County yield"
          valueLabel={`${formatNumber(scenario.stress.finalYield, 1)} bu/ac`}
          {...bounds.finalYield}
          value={stress.finalYield}
          onChange={(v) => setStress((prev) => ({ ...prev, finalYield: v }))}
          caption={yieldDev.text}
          captionOff={yieldDev.off}
        />
        <StressSlider
          label="Input costs"
          valueLabel={`${fm(scenario.stress.harvestCosts)}/ac`}
          {...bounds.harvestCosts}
          value={stress.harvestCosts}
          onChange={(v) => setStress((prev) => ({ ...prev, harvestCosts: v }))}
          caption={costDev.text}
          captionOff={costDev.off}
        />
      </Stack>

      <McoEcoScenarioChart scenario={scenario} />

      <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={2} sx={{ mt: 1 }}>
        <PaymentCard
          product="ECO"
          color={COLOR_ECO}
          watches="revenue"
          payment={scenario.eco.payment}
          protection={scenario.eco.protection}
          paymentFactor={scenario.eco.paymentFactor}
          estimates={scenario.estimates}
        />
        <PaymentCard
          product="MCO"
          color={COLOR_MCO}
          watches="margin"
          payment={scenario.mco.payment}
          protection={scenario.mco.protection}
          paymentFactor={scenario.mco.paymentFactor}
          estimates={scenario.estimates}
        />
      </Stack>
    </Box>
  );
}
